/**
 * Tasks AG Grid Component
 *
 * Clean, simple grid view with:
 * - Alternating row colors
 * - Plain text (no inline components)
 * - Quick search
 * - Column visibility toggle
 * - Filter dropdown
 * - Expandable subtasks inline
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Button, Space, Tooltip, Spin, Checkbox, message } from 'antd';
import {
  EyeOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import type {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  GridApi,
  RowClassParams,
} from 'ag-grid-community';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './blueprint-grid.css';

import type { ProcessInstance, ProcessStatus, Category } from '@/types';
import { useTheme } from '@contexts/ThemeContext';
import GridToolbar, { ColumnConfig, FilterConfig, ActiveFilter } from './GridToolbar';
import { instanceSubtasksApi, type InstanceSubtask, type SubtaskProgress } from '@api/subtasks';

// ============================================================================
// Types for expandable grid rows
// ============================================================================

// A row that can be either a task or a subtask
interface GridRow {
  id: string;
  rowType: 'task' | 'subtask';
  // For tasks
  task?: ProcessInstance;
  hasSubtasks?: boolean;
  subtaskProgress?: SubtaskProgress;
  // For subtasks
  subtask?: InstanceSubtask;
  parentTaskId?: string;
}

interface TasksGridProps {
  tasks: ProcessInstance[];
  loading: boolean;
  onViewTask: (task: ProcessInstance) => void;
  onStartTask?: (task: ProcessInstance) => void;
  onCompleteTask?: (task: ProcessInstance) => void;
  categories?: Category[];
  extraToolbarButtons?: React.ReactNode;
  showSubtasks?: boolean; // Enable expandable subtasks (default: true)
  initialFilters?: ActiveFilter[]; // For loading saved views
}

// Blueprint chip classes map status → CSS modifier class (blueprint-grid.css)
const statusChipClass: Record<ProcessStatus, string> = {
  pending: 'bp-status-pending',
  in_progress: 'bp-status-in_progress',
  completed: 'bp-status-completed',
  postponed: 'bp-status-postponed',
  delayed: 'bp-status-delayed',
  cancelled: 'bp-status-cancelled',
  ignored: 'bp-status-ignored',
};

const statusLabels: Record<ProcessStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Done',
  postponed: 'Postponed',
  delayed: 'Delayed',
  cancelled: 'Cancelled',
  ignored: 'Ignored',
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'ignored', label: 'Ignored' },
];

export default function TasksGrid({
  tasks,
  loading,
  onViewTask,
  onStartTask,
  onCompleteTask,
  categories = [],
  extraToolbarButtons,
  showSubtasks = true,
  initialFilters,
}: TasksGridProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark ?? false;
  const queryClient = useQueryClient();
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialFilters || []);

  // Apply initial filters from saved view (when URL params change)
  useEffect(() => {
    if (initialFilters && initialFilters.length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [initialFilters]);

  // Track which tasks are expanded to show subtasks
  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<string>>(new Set());

  // Cache for subtask data per task
  const [subtaskCache, setSubtaskCache] = useState<Record<string, {
    items: InstanceSubtask[];
    progress: SubtaskProgress;
  }>>({});

  // Fetch subtasks when a task is expanded
  const fetchSubtasks = useCallback(async (taskId: string) => {
    if (subtaskCache[taskId]) return; // Already cached
    try {
      const data = await instanceSubtasksApi.list(taskId);
      setSubtaskCache(prev => ({
        ...prev,
        [taskId]: data,
      }));
    } catch (error) {
      console.error('Failed to fetch subtasks:', error);
    }
  }, [subtaskCache]);

  // Toggle subtask completion
  const toggleSubtaskMutation = useMutation({
    mutationFn: ({ instanceId, subtaskId }: { instanceId: string; subtaskId: string }) =>
      instanceSubtasksApi.toggle(instanceId, subtaskId),
    onSuccess: (updatedSubtask, variables) => {
      // Update cache
      setSubtaskCache(prev => {
        const cached = prev[variables.instanceId];
        if (!cached) return prev;
        return {
          ...prev,
          [variables.instanceId]: {
            ...cached,
            items: cached.items.map(s =>
              s.id === variables.subtaskId ? updatedSubtask : s
            ),
            progress: {
              ...cached.progress,
              completed: updatedSubtask.isCompleted
                ? cached.progress.completed + 1
                : cached.progress.completed - 1,
            },
          },
        };
      });
      // Invalidate main queries
      queryClient.invalidateQueries({ queryKey: ['instances'] });
    },
    onError: () => {
      message.error('Failed to update subtask');
    },
  });

  // Handle expand/collapse toggle
  const handleToggleExpand = useCallback((taskId: string) => {
    setExpandedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
        // Fetch subtasks if not cached
        fetchSubtasks(taskId);
      }
      return next;
    });
  }, [fetchSubtasks]);

  // Handle subtask toggle
  const handleToggleSubtask = useCallback((instanceId: string, subtaskId: string) => {
    toggleSubtaskMutation.mutate({ instanceId, subtaskId });
  }, [toggleSubtaskMutation]);

  // Column configuration for toolbar
  const columnConfig: ColumnConfig[] = useMemo(() => [
    { field: 'template.title', headerName: 'Task', alwaysVisible: true },
    { field: 'status', headerName: 'Status' },
    { field: 'dueDate', headerName: 'Due' },
    { field: 'template.category', headerName: 'Category' },
    { field: 'template.tags', headerName: 'Tags' },
    { field: 'assignedUser', headerName: 'Assigned' },
    { field: 'actions', headerName: 'Actions' },
  ], []);

  // Filter configuration
  const filterConfig: FilterConfig[] = useMemo(() => [
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: statusOptions,
    },
    {
      field: 'category',
      label: 'Category',
      type: 'select',
      options: categories.map(cat => ({ value: cat.id, label: cat.name })),
    },
    {
      field: 'dueDate',
      label: 'Due Date',
      type: 'dateRange',
    },
  ], [categories]);

  // Filter tasks based on active filters
  const filteredTasks = useMemo(() => {
    if (activeFilters.length === 0) return tasks;
    return tasks.filter(task => {
      return activeFilters.every(filter => {
        if (filter.field === 'status') {
          return task.status === filter.value;
        }
        if (filter.field === 'category') {
          return task.template?.category?.id === filter.value;
        }
        if (filter.field === 'dueDate' && Array.isArray(filter.value)) {
          const dueDate = new Date(task.dueDate);
          const startDate = new Date(filter.value[0]);
          const endDate = new Date(filter.value[1]);
          return dueDate >= startDate && dueDate <= endDate;
        }
        return true;
      });
    });
  }, [tasks, activeFilters]);

  // Transform tasks + expanded subtasks into grid rows
  const gridRows: GridRow[] = useMemo(() => {
    if (!showSubtasks) {
      // Simple mode: just tasks
      return filteredTasks.map(task => ({
        id: task.id,
        rowType: 'task' as const,
        task,
      }));
    }

    const rows: GridRow[] = [];
    for (const task of filteredTasks) {
      const cached = subtaskCache[task.id];
      const hasSubtasks = cached ? cached.progress.total > 0 : false;

      // Add the task row
      rows.push({
        id: task.id,
        rowType: 'task',
        task,
        hasSubtasks,
        subtaskProgress: cached?.progress,
      });

      // If expanded, add subtask rows
      if (expandedTaskIds.has(task.id) && cached?.items) {
        for (const subtask of cached.items) {
          rows.push({
            id: `subtask-${subtask.id}`,
            rowType: 'subtask',
            subtask,
            parentTaskId: task.id,
          });
        }
      }
    }
    return rows;
  }, [filteredTasks, showSubtasks, expandedTaskIds, subtaskCache]);

  // Pre-fetch subtasks progress for all tasks when showSubtasks is enabled
  useEffect(() => {
    if (!showSubtasks || filteredTasks.length === 0) return;

    // Fetch subtasks for tasks not yet in cache (limit concurrent requests)
    const uncachedTasks = filteredTasks.filter(t => !subtaskCache[t.id]);
    const fetchBatch = async () => {
      // Fetch in batches to avoid overwhelming the API
      for (let i = 0; i < Math.min(uncachedTasks.length, 10); i++) {
        fetchSubtasks(uncachedTasks[i].id);
      }
    };
    fetchBatch();
  }, [showSubtasks, filteredTasks, subtaskCache, fetchSubtasks]);

  const columnDefs: ColDef<GridRow>[] = useMemo(
    () => [
      // Title column with expand/collapse caret and subtask checkbox
      {
        colId: 'title',
        headerName: 'Task',
        minWidth: 250,
        flex: 2,
        cellRenderer: (props: ICellRendererParams<GridRow>) => {
          const row = props.data;
          if (!row) return null;

          // Subtask row rendering
          if (row.rowType === 'subtask' && row.subtask) {
            const subtask = row.subtask;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: 32,
                  gap: 8,
                }}
              >
                <Checkbox
                  checked={subtask.isCompleted}
                  onChange={() => row.parentTaskId && handleToggleSubtask(row.parentTaskId, subtask.id)}
                />
                <span
                  style={{
                    textDecoration: subtask.isCompleted ? 'line-through' : 'none',
                    color: subtask.isCompleted ? 'var(--text-disabled)' : 'var(--text-secondary)',
                    fontSize: 13,
                  }}
                >
                  {subtask.title}
                </span>
                {subtask.isRequired && (
                  <span style={{ color: 'var(--status-warning)', fontSize: 11 }}>*</span>
                )}
              </div>
            );
          }

          // Task row rendering
          const task = row.task;
          if (!task) return null;

          const isExpanded = expandedTaskIds.has(task.id);
          const hasSubtasks = row.hasSubtasks;
          const progress = row.subtaskProgress;

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* Expand/collapse caret */}
              {showSubtasks && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(task.id);
                  }}
                  style={{
                    cursor: 'pointer',
                    width: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: hasSubtasks ? 'var(--status-info)' : 'var(--text-muted)',
                  }}
                >
                  {isExpanded ? (
                    <CaretDownOutlined style={{ fontSize: 12 }} />
                  ) : (
                    <CaretRightOutlined style={{ fontSize: 12 }} />
                  )}
                </span>
              )}

              {/* Task title */}
              <span
                style={{ fontWeight: 500, cursor: 'pointer', color: 'var(--status-info)', flex: 1 }}
                onClick={() => onViewTask(task)}
              >
                {task.template?.title || 'Untitled'}
              </span>

              {/* Subtask progress indicator */}
              {progress && progress.total > 0 && (
                <Tooltip title={`${progress.completed}/${progress.total} subtasks completed`}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      color: progress.completed === progress.total ? 'var(--status-success)' : 'var(--text-tertiary)',
                      background: 'var(--hover-bg)',
                      padding: '2px 6px',
                      borderRadius: 10,
                    }}
                  >
                    <CheckSquareOutlined style={{ fontSize: 11 }} />
                    {progress.completed}/{progress.total}
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
        valueGetter: (params) => {
          const row = params.data;
          if (row?.rowType === 'subtask') return row.subtask?.title || '';
          return row?.task?.template?.title || '';
        },
      },
      // Status column
      {
        colId: 'status',
        headerName: 'Status',
        width: 130,
        cellRenderer: (props: ICellRendererParams<GridRow>) => {
          const row = props.data;
          if (!row || row.rowType === 'subtask') return null;
          const task = row.task;
          if (!task) return null;

          const status = task.status as ProcessStatus;
          const isOverdue = status !== 'completed' && new Date(task.dueDate) < new Date();

          return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className={`bp-status-chip ${statusChipClass[status]}`}>
                {statusLabels[status]}
              </span>
              {isOverdue && (
                <span style={{ color: 'var(--status-error)', fontSize: 11 }}>⚠</span>
              )}
            </span>
          );
        },
        valueGetter: (params) => params.data?.task?.status || '',
      },
      // Due date column
      {
        colId: 'dueDate',
        headerName: 'Due',
        width: 120,
        cellRenderer: (props: ICellRendererParams<GridRow>) => {
          const row = props.data;
          if (!row || row.rowType === 'subtask') return null;

          const task = row.task;
          if (!task?.dueDate) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;

          const date = new Date(task.dueDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const dueDate = new Date(date);
          dueDate.setHours(0, 0, 0, 0);

          const isToday = dueDate.getTime() === today.getTime();
          const isPast = dueDate < today;
          const isCompleted = task.status === 'completed';

          const dateClass = isPast && !isCompleted
            ? 'bp-date-overdue'
            : isToday
              ? 'bp-date-today'
              : 'bp-date-normal';
          return (
            <span className={dateClass}>
              {isToday ? 'Today' : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}
            </span>
          );
        },
        valueGetter: (params) => params.data?.task?.dueDate || '',
        sort: 'asc',
      },
      // Category column
      {
        colId: 'category',
        headerName: 'Category',
        width: 120,
        cellRenderer: (props: ICellRendererParams<GridRow>) => {
          const row = props.data;
          if (!row || row.rowType === 'subtask') return null;

          const category = row.task?.template?.category;
          if (!category) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;

          return (
            <span
              style={{
                background: category.color || 'var(--hover-bg)',
                color: 'var(--surface-1)',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {category.name}
            </span>
          );
        },
        valueGetter: (params) => params.data?.task?.template?.category?.name || '',
      },
      // Tags column
      {
        colId: 'tags',
        headerName: 'Tags',
        width: 180,
        cellRenderer: (props: ICellRendererParams<GridRow>) => {
          const row = props.data;
          if (!row || row.rowType === 'subtask') return null;

          const tags = row.task?.template?.tags || [];
          if (tags.length === 0) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              {tags.slice(0, 2).map((tag) => (
                <span key={tag.id} className="bp-tag-chip">
                  <span
                    className="bp-tag-dot"
                    style={{ backgroundColor: tag.color || 'var(--text-tertiary)' }}
                  />
                  {tag.name}
                </span>
              ))}
              {tags.length > 2 && (
                <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                  +{tags.length - 2}
                </span>
              )}
            </div>
          );
        },
        valueGetter: (params) =>
          params.data?.task?.template?.tags?.map((t) => t.name).join(', ') || '',
      },
      // Assigned user column
      {
        colId: 'assignedUser',
        headerName: 'Assigned',
        width: 130,
        cellRenderer: (props: ICellRendererParams<GridRow>) => {
          const row = props.data;
          if (!row || row.rowType === 'subtask') return null;

          const name = row.task?.assignedUser?.fullName;
          return <span style={{ color: name ? 'inherit' : 'var(--text-tertiary)' }}>{name || '—'}</span>;
        },
        valueGetter: (params) => params.data?.task?.assignedUser?.fullName || '—',
      },
      // Actions column
      {
        colId: 'actions',
        headerName: '',
        width: 100,
        pinned: 'right',
        sortable: false,
        cellRenderer: (props: ICellRendererParams<GridRow>) => {
          const row = props.data;
          if (!row) return null;

          // No actions for subtask rows
          if (row.rowType === 'subtask') return null;

          const task = row.task;
          if (!task) return null;

          return (
            <Space size={4}>
              <Tooltip title="View">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => onViewTask(task)}
                />
              </Tooltip>
              {task.status === 'pending' && onStartTask && (
                <Tooltip title="Start">
                  <Button
                    type="text"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={() => onStartTask(task)}
                  />
                </Tooltip>
              )}
              {(task.status === 'pending' || task.status === 'in_progress') && onCompleteTask && (
                <Tooltip title="Complete">
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={() => onCompleteTask(task)}
                    style={{ color: 'var(--status-success)' }}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [onViewTask, onStartTask, onCompleteTask, showSubtasks, expandedTaskIds, handleToggleExpand, handleToggleSubtask]
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  }, []);

  // Get row class based on row type and overdue state
  const getRowClass = useCallback((params: RowClassParams<GridRow>) => {
    const classes: string[] = [];
    if (params.data?.rowType === 'subtask') {
      classes.push('subtask-row');
    }
    // Mark overdue task rows
    const task = params.data?.task;
    if (task && task.status !== 'completed' && new Date(task.dueDate) < new Date()) {
      classes.push('bp-row-overdue');
    }
    return classes.join(' ');
  }, []);

  // Show loading overlay
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: 300,
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar with search, columns, and filters */}
      <GridToolbar
        gridApi={gridApi}
        columns={columnConfig}
        filters={filterConfig}
        storageKey="tasks-grid"
        searchPlaceholder="Search tasks..."
        onSearchChange={setQuickFilterText}
        onFilterChange={setActiveFilters}
        quickFilterText={quickFilterText}
        extraButtons={extraToolbarButtons}
      />

      {/* Grid with custom styling */}
      <div
        className={`${isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'} bp-grid`}
        style={{
          flex: 1,
          minHeight: 400,
          ...({
            '--ag-background-color': 'var(--surface-1)',
            '--ag-odd-row-background-color': 'var(--row-alt, #f7f9fc)',
            '--ag-row-border-color': 'var(--border-subtle)',
            '--ag-header-background-color': 'var(--surface-1)',
            '--ag-border-color': 'var(--grid-line, #dde3ec)',
            '--ag-foreground-color': 'var(--text-primary)',
            '--ag-secondary-foreground-color': 'var(--text-secondary)',
            '--ag-row-hover-color': 'var(--hover-bg)',
            '--ag-selected-row-background-color': 'rgba(30, 64, 175, 0.10)',
            '--ag-header-foreground-color': 'var(--text-tertiary)',
          }),
        } as React.CSSProperties}
      >
        <AgGridReact<GridRow>
          ref={gridRef}
          rowData={gridRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          quickFilterText={quickFilterText}
          animateRows
          rowSelection="single"
          suppressCellFocus
          getRowId={(params) => params.data.id}
          getRowClass={getRowClass}
          domLayout="autoHeight"
          rowHeight={40}
          headerHeight={36}
        />
      </div>

      {/* Subtask row styles — kept inline as they depend on runtime theme class */}
      <style>{`
        .ag-theme-alpine-dark .subtask-row,
        .ag-theme-alpine .subtask-row {
          border-left: 3px solid var(--status-info) !important;
        }
        .ag-theme-alpine-dark .subtask-row {
          background-color: var(--surface-0) !important;
        }
        .ag-theme-alpine .subtask-row {
          background-color: var(--hover-bg) !important;
        }
        .ag-theme-alpine .subtask-row .ag-cell,
        .ag-theme-alpine-dark .subtask-row .ag-cell {
          font-size: 12px;
        }
      `}</style>
    </div>
  );
}
